'use client';

import { c as _c } from "react/compiler-runtime";
import { AppSearchBar } from '@/components/ui/app-search-bar';
import type { HofAreaSummary, HofFormState, HofOverviewRow } from '@/features/hof-dashboard/api/hof-overview';
import { AreaIcon, LABEL_CLASS, Panel, PRIMARY_BUTTON_CLASS, SECONDARY_BUTTON_CLASS, SectionHeading, SegmentBar, StatusPill, type SegmentTone } from '@/features/hof-dashboard/components/dashboard-ui';
import { HOF_DASHBOARD_AREA_LABELS, HOF_DASHBOARD_AREAS, HOF_ENTRY_STATUS_LABELS, type HofDashboardArea } from '@/features/hof-dashboard/constants';
import { useAcceptHofArea } from '@/features/hof-dashboard/hooks/use-accept-hof-area';
import { translate, translateCount, type TextKey } from '@/features/hof-dashboard/texts';
import type { Locale } from '@/types/types';
import { cn } from '@/utils/tailwindcss-override';
import { CheckCheck, ChevronRight, Download } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';

/** The areas a reviewer accepts at once; material orders are confirmed one by one. */
const ACCEPTED_AT_ONCE: readonly HofDashboardArea[] = ['infrastructure', 'program'];
const FILTERS = ['all', 'toReview', 'withSubmissions', 'nothing'] as const;
type Filter = (typeof FILTERS)[number];
const FILTER_LABELS: Record<Filter, TextKey> = {
  all: 'allHoefe',
  toReview: 'filterToReview',
  withSubmissions: 'filterWithSubmissions',
  nothing: 'filterNothing'
};
const SEGMENT_TONE: Record<HofFormState, SegmentTone> = {
  accepted: 'filled',
  waiting: 'waiting',
  missing: 'empty'
};

/** The table's columns where it has the room; a narrow screen stacks each Hof as a card. */
const ROW_GRID = '@3xl:grid @3xl:grid-cols-[minmax(10rem,1.4fr)_repeat(3,minmax(0,1fr))_6.5rem_5.5rem_1.25rem] @3xl:items-center @3xl:gap-x-6';
const toReviewOf = (hof: HofOverviewRow): number => HOF_DASHBOARD_AREAS.reduce((sum, area) => sum + hof.areas[area].toReview, 0);

/** A name as it is searched: lower case, without accents, so "zuri" finds "Züri 11". */
const searchable = (value: string): string => value.normalize('NFD').replaceAll(/\p{Diacritic}/gu, '').toLowerCase();
const matchesFilter = (hof: HofOverviewRow, filter: Filter): boolean => {
  switch (filter) {
    case 'toReview':
      {
        return toReviewOf(hof) > 0;
      }
    // every submission is in the Hof's ZIP, so it holds something once the Hof handed in
    case 'withSubmissions':
      {
        return hof.files > 0;
      }
    case 'nothing':
      {
        return hof.files === 0;
      }
    default:
      {
        return true;
      }
  }
};

/** What waits for the Ressort in one area of a Hof, in words. */
const waitingLine = (area: HofDashboardArea, n: number, locale: Locale): string => area === 'material' ? translateCount(n, 'orderedOne', 'orderedMany', locale) : translateCount(n, 'handedInOne', 'handedInMany', locale);

/**
 * One Hof with something to review, as a row: what waits, the way to it, and accepting all of
 * it. Only the first row's way in is the view's primary action.
 */
const WaitingRow: React.FC<{
  hof: HofOverviewRow;
  primary: boolean;
  accepting: boolean;
  onReview: (area: HofDashboardArea) => void;
  onAccept: () => void;
  locale: Locale;
}> = t0 => {
  const $ = _c(51);
  const {
    hof,
    primary,
    accepting,
    onReview,
    onAccept,
    locale
  } = t0;
  let first;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let toAccept;
  if ($[0] !== hof.areas || $[1] !== hof.name || $[2] !== locale) {
    let t6;
    if ($[10] !== hof.areas) {
      t6 = area => hof.areas[area].toReview > 0;
      $[10] = hof.areas;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    const waiting = HOF_DASHBOARD_AREAS.filter(t6);
    let t7;
    if ($[12] !== hof.areas) {
      t7 = (sum, area_0) => sum + hof.areas[area_0].toAccept;
      $[12] = hof.areas;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    toAccept = ACCEPTED_AT_ONCE.reduce(t7, 0);
    first = waiting[0];
    t5 = "flex flex-wrap items-center gap-x-4 gap-y-3 px-5 py-4 @xl:px-6";
    t3 = "min-w-0 flex-1 basis-64 space-y-1";
    if ($[14] !== hof.name) {
      t4 = <p className="text-base font-semibold text-green-900">{hof.name}</p>;
      $[14] = hof.name;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    t1 = "flex flex-wrap gap-x-4 gap-y-1";
    let t8;
    if ($[16] !== hof.areas || $[17] !== locale) {
      t8 = area_1 => <li key={area_1} className="flex items-center gap-1.5 text-sm text-green-700"><AreaIcon area={area_1} />{HOF_DASHBOARD_AREA_LABELS[area_1][locale]} ·{" "}{waitingLine(area_1, hof.areas[area_1].toReview, locale)}</li>;
      $[16] = hof.areas;
      $[17] = locale;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    t2 = waiting.map(t8);
    $[0] = hof.areas;
    $[1] = hof.name;
    $[2] = locale;
    $[3] = first;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = toAccept;
  } else {
    first = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
    toAccept = $[9];
  }
  let t6;
  if ($[19] !== t1 || $[20] !== t2) {
    t6 = <ul className={t1}>{t2}</ul>;
    $[19] = t1;
    $[20] = t2;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== t3 || $[23] !== t4 || $[24] !== t6) {
    t7 = <div className={t3}>{t4}{t6}</div>;
    $[22] = t3;
    $[23] = t4;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== hof || $[27] !== locale) {
    t8 = translate("toReviewCount", locale, {
      n: toReviewOf(hof)
    });
    $[26] = hof;
    $[27] = locale;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== t8) {
    t9 = <StatusPill tone="warning">{t8}</StatusPill>;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== first || $[32] !== hof.name || $[33] !== locale || $[34] !== onReview || $[35] !== primary) {
    t10 = first !== undefined && <button type="button" className={primary ? PRIMARY_BUTTON_CLASS : SECONDARY_BUTTON_CLASS} aria-label={translate("reviewHofLabel", locale, {
      hof: hof.name
    })} onClick={() => onReview(first)}>{translate("review", locale)}</button>;
    $[31] = first;
    $[32] = hof.name;
    $[33] = locale;
    $[34] = onReview;
    $[35] = primary;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  let t11;
  if ($[37] !== accepting || $[38] !== hof.name || $[39] !== locale || $[40] !== onAccept || $[41] !== toAccept) {
    t11 = toAccept > 0 && <button type="button" className={SECONDARY_BUTTON_CLASS} disabled={accepting} aria-label={translate("acceptHofLabel", locale, {
      hof: hof.name
    })} onClick={() => {
      const question = translate("acceptHofQuestion", locale, {
        n: toAccept,
        hof: hof.name
      });
      if (globalThis.confirm(question)) {
        onAccept();
      }
    }}><CheckCheck aria-hidden={true} />{translate("acceptArea", locale)}</button>;
    $[37] = accepting;
    $[38] = hof.name;
    $[39] = locale;
    $[40] = onAccept;
    $[41] = toAccept;
    $[42] = t11;
  } else {
    t11 = $[42];
  }
  let t12;
  if ($[43] !== t10 || $[44] !== t11) {
    t12 = <div className="flex flex-wrap gap-2">{t10}{t11}</div>;
    $[43] = t10;
    $[44] = t11;
    $[45] = t12;
  } else {
    t12 = $[45];
  }
  let t13;
  if ($[46] !== t12 || $[47] !== t5 || $[48] !== t7 || $[49] !== t9) {
    t13 = <li className={t5}>{t7}{t9}{t12}</li>;
    $[46] = t12;
    $[47] = t5;
    $[48] = t7;
    $[49] = t9;
    $[50] = t13;
  } else {
    t13 = $[50];
  }
  return t13;
};

/** How far a Hof is in one area: handed in of all, one mark per form. */
const AreaCell: React.FC<{
  area: HofDashboardArea;
  summary: HofAreaSummary;
  locale: Locale;
}> = t0 => {
  const $ = _c(27);
  const {
    area,
    summary,
    locale
  } = t0;
  if (summary.forms === 0) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span className="text-gray-400">–</span>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== summary.formStates) {
    t1 = state => summary.formStates.filter(candidate => candidate === state).length;
    $[1] = summary.formStates;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const count = t1;
  let t2;
  if ($[3] !== locale || $[4] !== summary.done || $[5] !== summary.forms) {
    t2 = translate("progressShort", locale, {
      done: summary.done,
      total: summary.forms
    });
    $[3] = locale;
    $[4] = summary.done;
    $[5] = summary.forms;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== locale || $[8] !== summary.overdue) {
    t3 = summary.overdue > 0 && <span className="text-danger-text">{" \xB7 "}{translate("overdueCount", locale, {
        n: summary.overdue
      })}</span>;
    $[7] = locale;
    $[8] = summary.overdue;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t2 || $[11] !== t3) {
    t4 = <p className="text-sm text-green-800 tabular-nums @min-[900px]/dashboard:pointer-fine:text-[14px]">{t2}{t3}</p>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== summary.formStates) {
    t5 = summary.formStates.map(_temp);
    $[13] = summary.formStates;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const t6 = HOF_DASHBOARD_AREA_LABELS[area][locale];
  let t7;
  if ($[15] !== count || $[16] !== locale || $[17] !== summary.done || $[18] !== summary.forms || $[19] !== t6) {
    t7 = translate("formStatesLabel", locale, {
      area: t6,
      done: summary.done,
      total: summary.forms,
      accepted: count("accepted"),
      waiting: count("waiting")
    });
    $[15] = count;
    $[16] = locale;
    $[17] = summary.done;
    $[18] = summary.forms;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t5 || $[22] !== t7) {
    t8 = <SegmentBar segments={t5} label={t7} />;
    $[21] = t5;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t4 || $[25] !== t8) {
    t9 = <div className="space-y-1.5 @min-[900px]/dashboard:pointer-fine:space-y-[4px]">{t4}{t8}</div>;
    $[24] = t4;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  return t9;
};
const LegendMark: React.FC<{
  className: string;
  label: string;
}> = t0 => {
  const $ = _c(7);
  const {
    className,
    label
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("h-1.5 w-5 rounded-full", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <span aria-hidden={true} className={t1} />;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== label || $[5] !== t2) {
    t3 = <span className="inline-flex items-center gap-2">{t2}{label}</span>;
    $[4] = label;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};

/**
 * Where every Hof stands, for the reviewers: first what waits for them, by Hof, with accepting
 * a Hof's Infrastruktur and Programm at once; then every Hof with how far it is per form,
 * searchable and filtered. A row opens the Hof's dashboard.
 */
export const HofOverviewTable: React.FC<{
  hoefe: HofOverviewRow[];
  onOpen: (hofId: string, area?: HofDashboardArea) => void;
  locale: Locale;
}> = t0 => {
  const $ = _c(65);
  const {
    hoefe,
    onOpen,
    locale
  } = t0;
  const {
    accept,
    pendingHofId
  } = useAcceptHofArea(locale);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== accept || $[1] !== filter || $[2] !== hoefe || $[3] !== locale || $[4] !== onOpen || $[5] !== pendingHofId || $[6] !== search) {
    const waiting = hoefe.filter(_temp2);
    const waitingTotal = waiting.reduce(_temp3, 0);
    const needle = searchable(search.trim());
    const shown = hoefe.filter(hof_1 => matchesFilter(hof_1, filter) && searchable(hof_1.name).includes(needle));
    t4 = "space-y-6";
    let t6;
    if ($[13] !== locale) {
      t6 = translate("waitingForReview", locale);
      $[13] = locale;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    let t7;
    if ($[15] !== t6) {
      t7 = <SectionHeading className="text-xl">{t6}</SectionHeading>;
      $[15] = t6;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    const t8 = waiting.length > 0 && <span className="text-sm text-green-700">{translateCount(waitingTotal, "submissionCountOne", "submissionCountMany", locale)}{" "}{translateCount(waiting.length, "fromHoefeOne", "fromHoefeMany", locale)}</span>;
    let t9;
    if ($[17] !== t7 || $[18] !== t8) {
      t9 = <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 p-5 @xl:px-6">{t7}{t8}</div>;
      $[17] = t7;
      $[18] = t8;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    const t10 = waiting.length === 0 ? <p className="px-5 pb-5 text-base text-green-700 @xl:px-6">{translate("nothingToReview", locale)}</p> : <ul className="divide-line border-line divide-y border-t">{waiting.map((hof_2, index) => <WaitingRow key={hof_2.id} hof={hof_2} primary={index === 0} accepting={pendingHofId === hof_2.id} onReview={area => onOpen(hof_2.id, area)} onAccept={() => void accept(hof_2.id, ACCEPTED_AT_ONCE)} locale={locale} />)}</ul>;
    if ($[20] !== t10 || $[21] !== t9) {
      t5 = <Panel className="p-0 @xl:p-0">{t9}{t10}</Panel>;
      $[20] = t10;
      $[21] = t9;
      $[22] = t5;
    } else {
      t5 = $[22];
    }
    T0 = Panel;
    t1 = "p-0 @xl:p-0";
    let t11;
    if ($[23] !== locale) {
      t11 = translate("searchHof", locale);
      $[23] = locale;
      $[24] = t11;
    } else {
      t11 = $[24];
    }
    let t12;
    let t13;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = event => setSearch(event.target.value);
      t13 = () => setSearch("");
      $[25] = t12;
      $[26] = t13;
    } else {
      t12 = $[25];
      t13 = $[26];
    }
    let t14;
    if ($[27] !== search || $[28] !== t11) {
      t14 = <AppSearchBar placeholder={t11} value={search} onChange={t12} onClear={t13} className="w-full @3xl:w-72" />;
      $[27] = search;
      $[28] = t11;
      $[29] = t14;
    } else {
      t14 = $[29];
    }
    let t15;
    if ($[30] !== locale) {
      t15 = translate("filters", locale);
      $[30] = locale;
      $[31] = t15;
    } else {
      t15 = $[31];
    }
    let t16;
    if ($[32] !== filter || $[33] !== locale) {
      t16 = FILTERS.map(candidate => <button key={candidate} type="button" aria-pressed={filter === candidate} className={cn("inline-flex min-h-11 cursor-pointer items-center rounded-full border px-4 text-sm font-semibold transition", filter === candidate ? "bg-conveniat-green border-conveniat-green text-white" : "border-line bg-white text-green-800 hover:bg-gray-50")} onClick={() => setFilter(candidate)}>{translate(FILTER_LABELS[candidate], locale)}</button>);
      $[32] = filter;
      $[33] = locale;
      $[34] = t16;
    } else {
      t16 = $[34];
    }
    let t17;
    if ($[35] !== t15 || $[36] !== t16) {
      t17 = <div role="group" aria-label={t15} className="flex flex-wrap gap-2">{t16}</div>;
      $[35] = t15;
      $[36] = t16;
      $[37] = t17;
    } else {
      t17 = $[37];
    }
    if ($[38] !== t14 || $[39] !== t17) {
      t2 = <div className="flex flex-wrap items-center gap-3 p-5 @xl:p-6">{t14}{t17}</div>;
      $[38] = t14;
      $[39] = t17;
      $[40] = t2;
    } else {
      t2 = $[40];
    }
    t3 = hoefe.length === 0 || shown.length === 0 ? <p className="border-line border-t px-5 py-6 text-base text-green-700 @xl:px-6">{translate(hoefe.length === 0 ? "noHoefe" : "noMatchingHof", locale)}</p> : <div className="border-line border-t"><div aria-hidden={true} className={cn("bg-page hidden px-6 py-3 @min-[900px]/dashboard:pointer-fine:py-[8px]", LABEL_CLASS, ROW_GRID)}><span>{translate("hof", locale)}</span>{HOF_DASHBOARD_AREAS.map(area_0 => <span key={area_0} className="flex items-center gap-2"><AreaIcon area={area_0} />{HOF_DASHBOARD_AREA_LABELS[area_0][locale]}</span>)}<span>{translate("filterToReview", locale)}</span><span>{translate("files", locale)}</span></div><ul className="divide-line divide-y">{shown.map(hof_3 => {
          const toReview = toReviewOf(hof_3);
          return <li key={hof_3.id} className={cn("relative space-y-3 px-5 py-4 transition hover:bg-gray-50 @xl:px-6 @3xl:space-y-0 @min-[900px]/dashboard:pointer-fine:py-[8px]", ROW_GRID)}><button type="button" className="cursor-pointer text-left text-base font-semibold text-green-900 after:absolute after:inset-0 focus-visible:outline-none focus-visible:after:outline-2 focus-visible:after:outline-offset-[-2px] focus-visible:after:outline-green-600 @min-[900px]/dashboard:pointer-fine:text-[15px]" aria-label={translate("openHof", locale, {
              hof: hof_3.name
            })} onClick={() => onOpen(hof_3.id)}>{hof_3.name}</button>{HOF_DASHBOARD_AREAS.map(area_1 => <div key={area_1} className="min-w-0"><p className={cn(LABEL_CLASS, "mb-1 @3xl:sr-only")}>{HOF_DASHBOARD_AREA_LABELS[area_1][locale]}</p><AreaCell area={area_1} summary={hof_3.areas[area_1]} locale={locale} /></div>)}<div className="flex flex-wrap items-center gap-3 @3xl:contents"><div>{toReview > 0 ? <StatusPill tone="warning">{translate("toReviewCount", locale, {
                    n: toReview
                  })}</StatusPill> : <span aria-hidden={true} className="hidden text-gray-400 @3xl:inline">–</span>}</div><div>{hof_3.files === 0 ? <span aria-hidden={true} className="hidden text-gray-400 @3xl:inline">–</span> : <a href={`/api/hof-dashboard/${encodeURIComponent(hof_3.id)}/files`} download={true} className="text-conveniat-green relative z-10 inline-flex min-h-11 items-center gap-1.5 text-sm font-semibold underline-offset-2 hover:underline @min-[900px]/dashboard:pointer-fine:min-h-[32px] @min-[900px]/dashboard:pointer-fine:text-[14px]" aria-label={translate("downloadFiles", locale, {
                  hof: hof_3.name
                })}><Download className="h-4 w-4" aria-hidden={true} />{translate("zipFiles", locale, {
                    n: hof_3.files
                  })}</a>}</div></div><ChevronRight aria-hidden={true} className="hidden h-5 w-5 justify-self-end text-green-500 @3xl:block" /></li>;
        })}</ul></div>;
    $[0] = accept;
    $[1] = filter;
    $[2] = hoefe;
    $[3] = locale;
    $[4] = onOpen;
    $[5] = pendingHofId;
    $[6] = search;
    $[7] = T0;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    T0 = $[7];
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
  }
  const t6 = HOF_ENTRY_STATUS_LABELS.accepted[locale];
  let t7;
  if ($[41] !== t6) {
    t7 = <LegendMark className="bg-conveniat-green" label={t6} />;
    $[41] = t6;
    $[42] = t7;
  } else {
    t7 = $[42];
  }
  let t8;
  if ($[43] !== locale) {
    t8 = translate("legendWaiting", locale);
    $[43] = locale;
    $[44] = t8;
  } else {
    t8 = $[44];
  }
  let t9;
  if ($[45] !== t8) {
    t9 = <LegendMark className="bg-amber-500" label={t8} />;
    $[45] = t8;
    $[46] = t9;
  } else {
    t9 = $[46];
  }
  let t10;
  if ($[47] !== locale) {
    t10 = translate("legendMissing", locale);
    $[47] = locale;
    $[48] = t10;
  } else {
    t10 = $[48];
  }
  let t11;
  if ($[49] !== t10) {
    t11 = <LegendMark className="bg-gray-100" label={t10} />;
    $[49] = t10;
    $[50] = t11;
  } else {
    t11 = $[50];
  }
  let t12;
  if ($[51] !== t11 || $[52] !== t7 || $[53] !== t9) {
    t12 = <div className="border-line flex flex-wrap gap-x-6 gap-y-2 border-t px-5 py-4 text-sm text-green-700 @xl:px-6">{t7}{t9}{t11}</div>;
    $[51] = t11;
    $[52] = t7;
    $[53] = t9;
    $[54] = t12;
  } else {
    t12 = $[54];
  }
  let t13;
  if ($[55] !== T0 || $[56] !== t1 || $[57] !== t12 || $[58] !== t2 || $[59] !== t3) {
    t13 = <T0 className={t1}>{t2}{t3}{t12}</T0>;
    $[55] = T0;
    $[56] = t1;
    $[57] = t12;
    $[58] = t2;
    $[59] = t3;
    $[60] = t13;
  } else {
    t13 = $[60];
  }
  let t14;
  if ($[61] !== t13 || $[62] !== t4 || $[63] !== t5) {
    t14 = <div className={t4}>{t5}{t13}</div>;
    $[61] = t13;
    $[62] = t4;
    $[63] = t5;
    $[64] = t14;
  } else {
    t14 = $[64];
  }
  return t14;
};
function _temp(state_0) {
  return SEGMENT_TONE[state_0];
}
function _temp2(hof) {
  return toReviewOf(hof) > 0;
}
function _temp3(sum, hof_0) {
  return sum + toReviewOf(hof_0);
}